import { useEffect, useState } from 'react'

import {
  type SeriesSpan,
  effectiveSpanDates,
  formatDayLabel,
  formatSpanRange,
} from '@/shared/domain'
import { Label, RadioGroup, RadioGroupItem } from '@/shared/ui/primitives'
import { ConfirmDialog } from './confirm-dialog'

export type SeriesScope = 'series' | 'day'

export type SeriesScopeAction = 'guides' | 'dogs' | 'remove'

export type SeriesScopeSubject = 'hunt' | 'booking'

export type SeriesScopeDialogProps = {
  open: boolean
  action: SeriesScopeAction
  span: SeriesSpan
  dayDate: string
  subject?: SeriesScopeSubject
  isBusy?: boolean
  errorMessage?: string | null
  onConfirm: (scope: SeriesScope) => void
  onCancel: () => void
}

const COPY: Record<SeriesScopeAction, { title: string; series: string; day: string }> = {
  guides: {
    title: 'Update guides',
    series: 'Assign these guides to every day',
    day: 'Assign these guides to this day only',
  },
  dogs: {
    title: 'Update dogs',
    series: 'Assign these dogs to every day',
    day: 'Assign these dogs to this day only',
  },
  remove: {
    title: 'Cancel hunt',
    series: 'Cancel the entire series',
    day: 'Remove this day only',
  },
}

const REMOVE_TITLE: Record<SeriesScopeSubject, string> = {
  hunt: 'Cancel hunt',
  booking: 'Cancel booking',
}

export function SeriesScopeDialog({
  open,
  action,
  span,
  dayDate,
  subject = 'hunt',
  isBusy = false,
  errorMessage,
  onConfirm,
  onCancel,
}: SeriesScopeDialogProps) {
  const [scope, setScope] = useState<SeriesScope>('day')

  useEffect(() => {
    if (open) setScope('day')
  }, [open])

  const dayCount = effectiveSpanDates(span).length
  const copy = COPY[action]
  const title = action === 'remove' ? REMOVE_TITLE[subject] : copy.title

  return (
    <ConfirmDialog
      open={open}
      title={title}
      description={`This ${subject} is part of a ${dayCount}-day series (${formatSpanRange(span)}). Do you want to change the entire series, or just ${formatDayLabel(dayDate)}?`}
      confirmLabel={scope === 'series' ? 'Apply to series' : 'Apply to this day'}
      variant={action === 'remove' ? 'destructive' : 'default'}
      isBusy={isBusy}
      errorMessage={errorMessage}
      onConfirm={() => onConfirm(scope)}
      onCancel={onCancel}
    >
      <RadioGroup
        value={scope}
        onValueChange={(value) => setScope(value as SeriesScope)}
        className="gap-3"
      >
        <div className="flex items-start gap-3">
          <RadioGroupItem value="day" id="series-scope-day" className="mt-0.5" />
          <Label htmlFor="series-scope-day" className="cursor-pointer font-normal leading-snug">
            <span className="block font-medium">Just this day</span>
            <span className="block text-sm text-muted-foreground">
              {copy.day} ({formatDayLabel(dayDate)}).
            </span>
          </Label>
        </div>
        <div className="flex items-start gap-3">
          <RadioGroupItem value="series" id="series-scope-series" className="mt-0.5" />
          <Label htmlFor="series-scope-series" className="cursor-pointer font-normal leading-snug">
            <span className="block font-medium">The entire series</span>
            <span className="block text-sm text-muted-foreground">
              {copy.series} ({formatSpanRange(span)}).
            </span>
          </Label>
        </div>
      </RadioGroup>
    </ConfirmDialog>
  )
}
